import { useEffect, useMemo, useState } from 'react';
import _isEmpty from 'lodash.isempty';
import {
  FormModal,
  Input,
  Text,
  getBucket,
  Image
} from '@shootsta/common-react';
import { CURRENCY_SYMBOL } from '@shootsta/common-domain-data';
import { replaceBucketKeysWithUrls } from '@shootsta/client-aws-s3';
import SHOOTSTA_ICON from '../assets/shootsta.png';
import { promoBonusFor, TopupPromo } from '../utils/topupPromo';

import '../styles/_top-up-credits-modal.scss';

interface ContractType {
  ORGANISATION_ID: string;
  CONTRACT_LINE_ITEMS: {
    STORAGE: number;
    SEATS: string | number;
    CREDITS: number;
  };
  RESET_PERIOD: string;
  BILLING_ENTITY_CODE: string;
  TOP_UP_CREDIT_VALUE: number;
  SALESFORCE_ACCOUNT_ID: string;
  CONTENT_REGION: string;
  TIER: 'PAID' | 'FREE' | 'TRIAL';
  START_DATE: string;
  END_DATE: string;
  MAX_PRODUCT_END_DATE: string;
}

interface ContractItemBalanceType {
  type: string;
  product: string;
  value: number;
}

interface TopupCreditsModalProps {
  imageUrl: string;
  organisationId?: string;
  apolloClient: any;
  visible: boolean;
  onClose: () => void;
  contractItemBalance: ContractItemBalanceType[];
  getContracts: ({
    product,
    status
  }: {
    product: string;
    status: string;
  }) => Promise<{ data: any; error: any }>;
  paymentCheckout: ({
    quantity,
    originUrl
  }: {
    quantity: number;
    originUrl: string;
  }) => Promise<{ data: any; error: any }>;
  financeLedgerEnabled?: boolean;
  promo?: TopupPromo | null;
  getFinanceBalance?: (args: Record<string, never>) => Promise<{
    data: any;
    error: any;
  }>;
}

function TopupCreditsModal({
  imageUrl,
  visible,
  onClose,
  apolloClient,
  organisationId,
  contractItemBalance,
  getContracts,
  paymentCheckout,
  financeLedgerEnabled = false,
  promo = null,
  getFinanceBalance
}: TopupCreditsModalProps) {
  const [contract, setContract] = useState<ContractType>({});
  const [credits, setCredits] = useState<number>(0);
  const [orgImageUrl, setOrgImageUrl] = useState<string>(SHOOTSTA_ICON);
  const [financeCreditsRemaining, setFinanceCreditsRemaining] = useState<
    number | null
  >(null);
  // Explicit load state so the balance line can render loading / error UI
  // when finance-ledger-enabled is on, instead of silently falling back
  // to the legacy `totalBalance` (which masks ledger bugs and indefinitely
  // blocks legacy-code deletion).
  const [financeBalanceState, setFinanceBalanceState] = useState<
    'idle' | 'loading' | 'ready' | 'error'
  >('idle');

  const totalBalance = useMemo(() => {
    if (_isEmpty(contractItemBalance)) return 0;

    const allCreditTypeBalances = getBucket(contractItemBalance);

    if (_isEmpty(allCreditTypeBalances)) return 0;

    const totalCreditsBalance = allCreditTypeBalances
      .map((item: any) => item.credit)
      .reduce((prev: number, next: number) => prev + next);

    const totalTopupCreditsBalance = allCreditTypeBalances
      .map((item: any) => item.topUpCredit)
      .reduce((prev: number, next: number) => prev + next);

    return totalCreditsBalance + totalTopupCreditsBalance;
  }, [contractItemBalance]);

  const fetchContractDetails = async () => {
    const { data, error } = await getContracts({
      product: 'WORKSPACE',
      status: 'ACTIVE'
    });

    if (!data || error) return;

    if (data?.getContracts?.[0]) {
      setContract(data.getContracts[0].Payload?.subscription);
    }
  };

  const getPreSignedOrgImageUrl = async () => {
    if (!imageUrl) {
      return;
    }

    // there is a problem with replacing imageUrl with pre-signed URL
    // with common-react where in it only replaces the bucket key if you have a valid membership
    // for that organisation
    if (imageUrl && !imageUrl.startsWith('organisation/')) {
      // If the imageUrl is not a bucket key meaning it has been replaced, we can directly set it
      setOrgImageUrl(imageUrl);
      return;
    }

    try {
      const { imageUrl: preSignedOrgImageUrl } =
        await replaceBucketKeysWithUrls({
          apiClient: apolloClient,
          data: { imageUrl },
          substitutedKeys: ['imageUrl'],
          organisationId
        });

      setOrgImageUrl(preSignedOrgImageUrl);
    } catch {
      // Silently handle error
    }
  };

  const handleKeyDown = async (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();

    await checkout();
  };

  const checkout = async () => {
    const { data, error } = await paymentCheckout({
      quantity: Number(credits),
      originUrl: window.location.href
    });

    if (!data || error) return;

    window.location.href = data.paymentCheckout.checkoutUrl;
  };

  function renderCreditPrice(amount: number, entityCode: string): string {
    const currency = CURRENCY_SYMBOL[entityCode];

    if (amount > 0 && !currency) {
      return `$${amount.toFixed(2)}`;
    }

    if (amount > 0 && currency) {
      return `${currency} ${amount.toFixed(2)}`;
    }

    return '-';
  }

  const pricePerCredit = contract?.TOP_UP_CREDIT_VALUE || 0;
  const billingEntityCode = contract?.BILLING_ENTITY_CODE || '-';
  const currencySymbol =
    CURRENCY_SYMBOL[billingEntityCode]?.split(' ')[1] ?? '';

  const promoBonus = promoBonusFor(Number(credits) || 0, promo);

  const calculatedDueAmount = useMemo(
    () => (Number(credits) || 0) * pricePerCredit,
    [credits, pricePerCredit]
  );

  useEffect(() => {
    fetchContractDetails();
    getPreSignedOrgImageUrl();
  }, []);

  useEffect(() => {
    if (!financeLedgerEnabled || !getFinanceBalance) return undefined;
    let cancelled = false;
    setFinanceBalanceState('loading');
    (async () => {
      try {
        const { data, error } = await getFinanceBalance({});
        if (cancelled) return;
        if (error) {
          // eslint-disable-next-line no-console
          console.error('Finance balance fetch returned error:', error);
          setFinanceBalanceState('error');
          return;
        }
        const balance = data?.getFinanceBalance;
        if (!balance || typeof balance.totalRemaining !== 'number') {
          setFinanceBalanceState('error');
          return;
        }
        setFinanceCreditsRemaining(balance.totalRemaining);
        setFinanceBalanceState('ready');
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('Finance balance fetch failed:', err);
        setFinanceBalanceState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [financeLedgerEnabled, getFinanceBalance]);

  // When finance-ledger-enabled is on, the Current Balance line renders
  // ONLY from the finance ledger — explicit loading / error states; never
  // a silent fallback to the legacy contractItemBalance sum.
  const balanceLine: string = financeLedgerEnabled
    ? financeBalanceState === 'ready' && financeCreditsRemaining !== null
      ? `${financeCreditsRemaining} credits remaining`
      : financeBalanceState === 'error'
        ? 'Balance unavailable — refresh to retry'
        : 'Loading balance…'
    : `${totalBalance} credits remaining`;

  return (
    <FormModal
      key="top-up-credit-modal"
      visible={visible}
      title="Top up Credits"
      onSave={checkout}
      saveDisabled={!credits}
      onClose={onClose}
      saveText="Proceed to Payment"
    >
      <div className="top-up-credits-modal">
        <div className="top-up-credits-modal__balance">
          {orgImageUrl && (
            <Image
              imageUrl={orgImageUrl}
              width="48px"
              height="48px"
              backgroundSize="cover"
              rounded
            />
          )}
          <div className="top-up-credits-modal__balance__title">
            <Text heading5 className="current-balance-title">
              Current Balance
            </Text>
            <Text className="credits-remaining" body>
              {balanceLine}
            </Text>
          </div>
        </div>
        <div className="top-up-credits-modal__purchase-box">
          <div className="top-up-credits-modal__purchase-box__quantity">
            <Text heading5>Purchase Quantity</Text>
            <div className="top-up-credits-modal__purchase-box__quantity__input">
              <Input
                type="number"
                min={0}
                onKeyDown={handleKeyDown}
                placeholder="e.g. 10"
                label="Number of credits"
                value={credits}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCredits(e.target.value)
                }
              />
              <span className="credit-suffix">credits</span>
            </div>
          </div>
          <div className="top-up-credits-modal__purchase-box__summary">
            <Text heading5>Summary</Text>
            <Text className="price-per-credit" body>
              {currencySymbol}
              {pricePerCredit}/credit
            </Text>
            <div className="top-up-credits-modal__purchase-box__summary__credit-amount">
              <Text>{credits || 0} credits</Text>
              <Text timestamp className="credit-price">
                {renderCreditPrice(calculatedDueAmount, billingEntityCode)}
              </Text>
            </div>
            {promoBonus > 0 && (
              <div className="top-up-credits-modal__purchase-box__summary__promo-bonus">
                <Text body>+{promoBonus} free</Text>
                <Text timestamp>Promo</Text>
              </div>
            )}
            <hr className="separator" />
          </div>
          <div className="top-up-credits-modal__purchase-box__amount-due">
            <Text heading5>Amount Due</Text>
            <Text heading5>
              {renderCreditPrice(calculatedDueAmount, billingEntityCode)}
            </Text>
          </div>
        </div>
      </div>
    </FormModal>
  );
}

export default TopupCreditsModal;
